import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import deepmerge from 'deepmerge';

import useNotification from '../../../../core/hooks/use_notification';
import { getProductsForPromotionAction, getProductsForPromotionState } from '../../../../features/product/get_products_for_promotion';
import { getProductsForPromotionNotificationData } from '../../../../core/utils/notification_utils';
import ProductFilters from '../common/product_filters';
import PaginationControls from '../../../common/pagination_controls';
import { GET_ROUTE_EXPIRATION_PROMOTIONS_DETAILS } from '../../../../core/constants';
import { getDefaultProductPaginationQueryValues } from '../../../../core/utils/api_utils';
import BlockIcon from "@mui/icons-material/Block";

const initialQueryParams = {
    ...getDefaultProductPaginationQueryValues,
    query: '',
};

const ExpirationPromotionsList = ({ tab }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();

    const getProductsForPromotion = useSelector(getProductsForPromotionState);

    const [queryParams, setQueryParams] = useState(location?.state?.queryParams || initialQueryParams);

    useEffect(() => {
        dispatch(getProductsForPromotionAction(queryParams))
            .then(response => showErrorMessage(getProductsForPromotionNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
            <Box>
                <ProductFilters
                    queryParams={ queryParams }
                    setQueryParams={ setQueryParams }
                />
            </Box>

            { getProductsForPromotion?.loading && <LinearProgress/> }

            {
                getProductsForPromotion?.data?.content &&
                <>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>Nazwa produktu</TableCell>
                                    <TableCell align="right" sx={ { pr: 2 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    getProductsForPromotion?.data?.content?.map(product => (
                                        <TableRow
                                            key={ product?.id }
                                            hover
                                            sx={ { cursor: 'pointer' } }
                                            onClick={ () => navigate(GET_ROUTE_EXPIRATION_PROMOTIONS_DETAILS(product?.id), {
                                                state: {
                                                    queryParams,
                                                    tab
                                                }
                                            }) }
                                        >
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: product?.active === false ? 'text.secondary' : 'text.primary'
                                                        }}
                                                    >
                                                        { product?.name }
                                                    </Typography>

                                                    { product?.active === false && (
                                                        <Tooltip title="Produkt jest nieaktywny" placement="top">
                                                            <BlockIcon fontSize="small" color="disabled" />
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right" sx={ { pr: 2, color: 'text.secondary' } }>
                                                <ChevronRightIcon fontSize="small"/>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={ { display: 'flex', justifyContent: 'center', mt: 2 } }>
                        <PaginationControls
                            currentPage={ getProductsForPromotion?.data?.pageable?.pageNumber }
                            totalPages={ getProductsForPromotion?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </>
            }
        </Box>
    );
};

export default ExpirationPromotionsList;